import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAppContext } from '@/context/AppContext';

/**
 * Resources Login Page
 * Route: /resources/login
 * Simple password entry form for accessing protected resources
 */
function ResourcesLoginPage() {
    const navigate = useNavigate();
    const { content } = useAppContext();
    const properties = content?.properties || {};
    
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(true);

    const siteTitle = properties.site_title || 'Resources';
    // Follows the site theme rather than reading the legacy colour properties.
    const themeBackground = 'var(--color-background)';
    const themeText = 'var(--color-text)';

    // Check if already authenticated on mount
    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('resources-session-token');
            if (!token) {
                setChecking(false);
                return;
            }

            try {
                const response = await fetch(`/api/resources/auth?token=${encodeURIComponent(token)}`);
                const data = await response.json();
                
                if (data.authorized) {
                    // Already authenticated, redirect to resources
                    navigate('/resources');
                } else {
                    // Token invalid, clear it
                    localStorage.removeItem('resources-session-token');
                    setChecking(false);
                }
            } catch (err) {
                console.error('Auth check failed:', err);
                localStorage.removeItem('resources-session-token');
                setChecking(false);
            }
        };

        checkAuth();
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password) return;

        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/resources/auth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });
            const data = await response.json();

            if (data.authorized && data.token) {
                // Store token and redirect
                localStorage.setItem('resources-session-token', data.token);
                navigate('/resources');
            } else {
                setError(data.error || 'Invalid password');
                setLoading(false);
            }
        } catch (err) {
            console.error('Login failed:', err);
            setError('Login failed. Please try again.');
            setLoading(false);
        }
    };

    // Show nothing while checking auth
    if (checking) {
        return (
            <div className="h-screen w-screen bg-gray-100 flex items-center justify-center">
                <div className="text-gray-400">Loading...</div>
            </div>
        );
    }

    return (
        <div className="h-screen w-screen bg-gray-100 flex flex-col items-center justify-center">
            <Helmet>
                <title>Resources Login - {siteTitle}</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex flex-col items-center justify-center mb-10 w-full">
                    <LogoImage />
                </div>

                {/* Login form */}
                <form 
                    onSubmit={handleSubmit} 
                    className=""
                    aria-label="Resources login form"
                >
                    <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
                        <div className="px-4 py-2.5">
                            <label htmlFor="password" className="block text-xs text-gray-400 mb-0.5 font-sansumi">Password</label>
                            <input 
                                id="password"
                                type="password" 
                                className="w-full text-base text-gray-800 bg-transparent focus:outline-none font-worksans" 
                                required 
                                autoFocus
                                autoComplete="current-password"
                                value={password} 
                                onChange={(e) => setPassword(e.target.value)} 
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="mt-5 flex items-center justify-between">
                        {error ? (
                            <span className="text-red-500 text-sm font-worksans ml-5">
                                {error}
                            </span>
                        ) : <span />}
                        <button 
                            type="submit" 
                            className="px-6 py-2 bg-gray-800 text-white text-sm font-worksans rounded-lg hover:bg-gray-700 transition-colors cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading || !password}
                        >
                            {loading ? 'Verifying...' : 'Login'}
                        </button>
                    </div>
                </form>
                
                {/* Back link */}
                <div className="mt-8 text-center">
                    <button
                        onClick={() => navigate('/')}
                        className="text-sm text-gray-600 hover:text-gray-800 transition-colors bg-transparent border-0 cursor-pointer font-worksans"
                    >
                        ← Back to site
                    </button>
                </div>
            </div>
        </div>
    );
}

// Logo component that tries multiple paths
function LogoImage() {
    const [logoUrl, setLogoUrl] = useState('/assets/logo.svg');
    const [loaded, setLoaded] = useState(false);
    
    useEffect(() => {
        const tryLogoPaths = ['/assets/logo.svg', '/logo.svg'];
        let currentIndex = 0;
        
        const tryNextLogo = () => {
            if (currentIndex >= tryLogoPaths.length) {
                setLogoUrl(null);
                return;
            }
            
            const img = new Image();
            img.onload = () => {
                setLogoUrl(tryLogoPaths[currentIndex]);
                setLoaded(true);
            };
            img.onerror = () => {
                currentIndex++;
                tryNextLogo();
            };
            img.src = tryLogoPaths[currentIndex];
        };
        
        tryNextLogo();
    }, []);
    
    if (!logoUrl || !loaded) {
        return null;
    }
    
    return (
        <img 
            src={logoUrl} 
            alt="Logo" 
            className="h-[7em] object-contain"
            style={{
                filter: 'brightness(0) saturate(100%) invert(25%)',
            }}
        />
    );
}

export default ResourcesLoginPage;
